import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';
import clsx from 'clsx';

interface FormFieldProps {
  label: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  children: (id: string, invalid: boolean) => ReactNode;
}

/**
 * Light-themed form field with label + hint/error slot. Use inside Base UI
 * flows. The dark `Field.tsx` remains for legacy dark dashboards.
 */
export function FormField({ label, hint, error, required, children }: FormFieldProps): JSX.Element {
  const id = useId();
  const invalid = Boolean(error);
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium" style={{ color: 'var(--bui-text-secondary)' }}>
        {label}
        {required && (
          <span aria-hidden className="ml-0.5" style={{ color: 'var(--bui-danger)' }}>
            *
          </span>
        )}
      </label>
      {children(id, invalid)}
      {error ? (
        <p className="text-[11px]" role="alert" style={{ color: 'var(--bui-danger)' }}>
          {error}
        </p>
      ) : hint ? (
        <p className="text-[11px]" style={{ color: 'var(--bui-text-secondary)' }}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

const baseInputClasses = clsx(
  'h-9 w-full border bg-white px-3 text-sm outline-none transition-colors',
  'placeholder:text-[var(--bui-text-placeholder)] focus-visible:ring-2',
  'disabled:cursor-not-allowed disabled:opacity-60',
);

const inputRadiusStyle = { borderRadius: 'var(--bui-radius-control, 10px)' } as const;

interface FormTextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const FormTextInput = forwardRef<HTMLInputElement, FormTextInputProps>(function FormTextInput(
  { invalid, className, style, ...rest },
  ref,
): JSX.Element {
  return (
    <input
      ref={ref}
      {...rest}
      className={clsx(baseInputClasses, className)}
      style={{
        borderColor: invalid ? 'var(--bui-danger)' : 'var(--bui-border)',
        color: 'var(--bui-text-primary)',
        ...inputRadiusStyle,
        ...style,
      }}
    />
  );
});

interface FormTextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const FormTextArea = forwardRef<HTMLTextAreaElement, FormTextAreaProps>(function FormTextArea(
  { invalid, className, style, ...rest },
  ref,
): JSX.Element {
  return (
    <textarea
      ref={ref}
      {...rest}
      className={clsx(
        'min-h-[88px] w-full border bg-white px-3 py-2 text-sm outline-none transition-colors',
        'placeholder:text-[var(--bui-text-placeholder)] focus-visible:ring-2',
        'disabled:cursor-not-allowed disabled:opacity-60',
        className,
      )}
      style={{
        borderColor: invalid ? 'var(--bui-danger)' : 'var(--bui-border)',
        color: 'var(--bui-text-primary)',
        ...inputRadiusStyle,
        ...style,
      }}
    />
  );
});
